import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes, { any, node } from 'prop-types';
import { CodeJar } from 'codejar';
import hljs from 'highlight.js';
import 'highlight.js/styles/atom-one-dark.css';
import { FlowContext } from '@/store/context/ReactFlowContext';
import { ReactFlow, addEdge, MiniMap, Controls, Handle, Position, Background, useNodesState, useUpdateNodeInternals, useEdgesState } from '@xyflow/react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import {Textarea} from '@/components/ui/textarea';
import { AddNodes } from '@/store/actions';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { javascript } from '@codemirror/lang-javascript';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { IconCirclePlus, IconX, IconTrash } from '@tabler/icons-react';
import toolsApi from '@/api/tools';
import useApi from '@/hooks/useApi';
import CodeMirror from '@uiw/react-codemirror';
import CanvasNode from '../canvas/CanvasNode';
import { getUniqueNodeId, initNode } from '../../utils/genericHelper';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@/store';
import { ToastAction } from "@/components/ui/toast"
import { useToast } from "@/hooks/use-toast"
import { useNavigate } from 'react-router-dom';

const nodeTypes = {
  custom: CanvasNode,
};

interface ToolEditModalProps {
  openToolEditModal: boolean;
  setOpenToolEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  getAllNodeOnclick: () => void;
}


interface OutputField {
  label: string;
  type: string;
  name: string;
  visibilityType: string;
  id: string;
}


interface InputControlField {
  label: string;
  name: string;
  id: string;
}

interface OutputControlField {
  label: string;
  name: string;
  id: string;
}

const initialNodeData = {
  id: '',
  displayName: 'create node',
  name: "create node",
  version: '1.0.0',
  created: new Date(),
  updated: new Date(),
  type: 'custom',
  logoUrl: '',
  icon: '',
  category: 'custom',
  description: '',
  minimumSupportedRelease: '1.0.0',
  maximumSupportedRelease: '2.0.0',
  inputParams: [],
  inputAnchors: [],
  outputs: [],
  outputAnchors: [],
  controlInputs: [
      {
          label: 'default',
          name: 'default'
      },
  ],
  controlOutputs: [
        {
          label: 'default',
          name: 'default'
      }
  ]
};


const CustomTabPanel: React.FC<{ children: React.ReactNode; value: number; index: number }> = ({ children, value, index, ...other }) => {
  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && (
        <div className="p-0">
          <p>{children}</p>
        </div>
      )}
    </div>
  );
};

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const ToolEditModal: React.FC<ToolEditModalProps> = ({ openToolEditModal, setOpenToolEditModal, getAllNodeOnclick }) => {
  const [codeEditor, setCodeEditor] = useState<HTMLDivElement | null>(null);
  const [value, setValue] = useState(0);
  const [editorContent, setEditorContent] = useState('');
  const [checkSaved, setCheckSaved] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);
  const [nodeData, setNodeData] = useState(initialNodeData);
  const codeEditorRef = useRef(null);
  const { loading, data, error } = useSelector((state:any) => state.addNodesReducer);
  const { reactFlowInstance, setReactFlowInstance } = useContext(FlowContext);
  const [successLogicValue, setSuccessLogicValue] = useState('')
  const createNewTool = useApi(toolsApi.createNewTool);
  const [selectedField, setSelectedField] = useState<any>();
  const [selectedOutputField, setSelectedOutputField] = useState<any>();
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast()
  const [triggerEffect, setTriggerEffect] = useState(false); // State to trigger useEffect
  const isInitialized = useRef(false);


  const [formData, setFormData] = useState({
    additionalParams: false,
    description: '',
    placeholder: '',
    visibilityType: '',
    label: "",
    name: "",
    type: "",
    id: "", 
  });

  const [outputFormData, setOutputFormData] = useState <OutputField>({
    label: '',
    type: '',
    name: '',
    visibilityType: '',
    id: "", 
  });

  const listField = (param) => {
    setSelectedField(param);
    setFormData(param);
    setSelectedInputControl(null);
  };


  const OutputlistField = (param) => {
    setSelectedOutputField(param);
    setOutputFormData(param);
    selectedOutputControl(null)
  };



  const [selectedInputControl, setSelectedInputControl] = useState<any>();
  const [inputControlFormData, setInputControlFormData] = useState <InputControlField>({
      label: '',
      name: '',
      id: "", 
    });
  
  const [selectedOutputControl, setSelectedOutputControl] = useState<any>();
  const [outputControlFormData, setOutputControlFormData] = useState <OutputControlField>({
      label: '',
      name: '',
      id: "", 
    });

  const SelectedInputControl = (param) => {
    setSelectedInputControl(param);
    setInputControlFormData(param);
    setSelectedField(null)
  };

  const handleInputConstrolChange = (e) => {
      const { name, value } = e.target; 

      setInputControlFormData((prev) => {
        const updatedFormData = { ...prev, [name]: value };
        return updatedFormData;
      });

      if (selectedInputControl) {
        // let isUpdated = false;
        // Clone nodeData safely to avoid mutation
        const newNodeData = structuredClone(nodeData);

        newNodeData.controlInputs = newNodeData.controlInputs.map((item: any) => {

          if (item.id === selectedInputControl.id) {
            const updatedItem = { 
              ...item, 
              [name]: value 
            };
            // isUpdated = JSON.stringify(updatedItem) !== JSON.stringify(item);
            return updatedItem;
          }
          return item;
        });

        isInitialized.current = false;
        setNodeData(newNodeData);
        setTriggerEffect((prev) => !prev);
    
      }
  }

  const handleOutputConstrolChange = (e) => {
      const { name, value } = e.target; 

      setOutputControlFormData((prev) => {
        const updatedFormData = { ...prev, [name]: value };
        return updatedFormData;
      });

      if (selectedOutputControl) {
        // let isUpdated = false;
        // Clone nodeData safely to avoid mutation
        const newNodeData = structuredClone(nodeData);

        newNodeData.controlOutputs = newNodeData.controlOutputs.map((item: any) => {


          if (item.id === selectedOutputControl.id) {
            const updatedItem = { 
              ...item, 
              [name]: value 
            };
            // isUpdated = JSON.stringify(updatedItem) !== JSON.stringify(item);
            return updatedItem;
          }
          return item;
        });

        isInitialized.current = false;
        setNodeData(newNodeData);
        setTriggerEffect((prev) => !prev);
    
      }
  }

  const SelectedOutputControl = (param) => {
    setSelectedOutputControl(param);
    setOutputControlFormData(param);
    setSelectedOutputField(null)
  };

  const deleteInputControl = (id: any) => {
    isInitialized.current = false;
    // Clone the nodeData object to avoid direct mutations
    const newNodeData = structuredClone(nodeData);
    // Filter out the inputParam with the matching id
    newNodeData.controlInputs = newNodeData.controlInputs.filter((param: any) => param.id !== id); 
  // Update the state with the new nodeData
  setNodeData(newNodeData);
  setSelectedField(null);
  setTriggerEffect((prev) => !prev);
  };


  const [toolName, setToolName] = useState('')

  const handleChangeForm = (e) => {
    
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;
  
    // Update formData state
    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: fieldValue };
  
      if (name === "visibilityType") {
        if (value === "Anchor") {
          updatedFormData.type = prev.label;  // Use label for Anchor
        } else if (value === "Parameter") {
          updatedFormData.type = ""; // Clear type when switching to Parameter
        }
      }
  
      if (name === "label" && prev.visibilityType === "Anchor") {
        updatedFormData.type = value; // Sync type with label if Anchor
      }
  
      return updatedFormData;
    });
  
    // Update nodeData when the selectedField exists
    if (selectedField) {
      let isUpdated = false; // Track if any change occurs
      const newNodeData: any = structuredClone(nodeData); 
      // Update inputParams
      newNodeData.inputParams = newNodeData.inputParams.map((item) => {
        if (item.id === selectedField?.id) {
          const updatedItem = { 
            ...item, 
            [name]: fieldValue 
          };
    
          // Logic for "visibilityType"
          if (name === "visibilityType") {
            updatedItem.type = fieldValue === "Anchor" ? updatedItem.name : updatedItem.type;
          }

          isUpdated = JSON.stringify(updatedItem) !== JSON.stringify(item);
    
          return updatedItem;
        }
        return item;
      });


      newNodeData.inputAnchors = newNodeData.inputAnchors.map((item) => {
        if (item.id === selectedField?.id) {

          const updatedItem = { 
            ...item, 
            [name]: fieldValue 
          };

          isUpdated = JSON.stringify(updatedItem) !== JSON.stringify(item);
    
          return updatedItem;
        }
        return item;
      });


      if (isUpdated) {
        isInitialized.current = false;
        setNodeData(newNodeData);
        setTriggerEffect((prev) => !prev);
      }

    }
    
  };

  const handleOutputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setOutputFormData((prev) => {
      const updatedFormData = { ...prev, [name]: fieldValue };

      return updatedFormData;
    });

  // }
  
    if (selectedOutputField) {
      let isUpdated = false;
      // Clone nodeData safely to avoid mutation
      const newNodeData: any = structuredClone(nodeData);

      newNodeData.outputAnchors = newNodeData.outputAnchors.map((item) => {
        if (item.id === selectedOutputField?.id) {
          const updatedItem = { 
            ...item, 
            [name]: fieldValue 
          };
          isUpdated = JSON.stringify(updatedItem) !== JSON.stringify(item);
          return updatedItem;
        }
        return item;
      });

      if (isUpdated) {
        isInitialized.current = false;
        setNodeData(newNodeData);
        setTriggerEffect((prev) => !prev);
      }

    }
  }

  const handleNodeChange = (e) => {
    isInitialized.current = false; // Reset initialization state
    const { name, value } = e.target;

    // Clone the current nodeData
    const newNodeData = structuredClone(nodeData);

    newNodeData[name] = value;
    // Use the functional update to safely set state
    setNodeData(newNodeData);
  };


  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleClose = () => {
    setOpenToolEditModal(false);
  };

  const handleSave = async () => {
    try {
      const mergedInputs = [...nodeData.inputAnchors, ...nodeData.inputParams];

      // Destructure nodeData to exclude inputAnchors, inputParams, and outputAnchors
      const { inputAnchors, inputParams, outputAnchors, controlInputs, controlOutputs, ...rest } = nodeData;

      // Add 'id' to each controlInput and controlOutput element
      const formattedControlInputs = controlInputs.map((input, i) => ({
          ...input,
          id: `${nodeData.displayName}_${i}-input-control-${input.name}-${input.name}`
      }));

      const formattedControlOutputs = controlOutputs.map((output, i) => ({
          ...output,
          id: `${nodeData.displayName}_${i}-output-control-${output.name}-${output.name}`
      }));

      // Create the new formatted object
      const formattedData = {
          ...rest,
          inputs: mergedInputs,
          outputs: outputAnchors,
          controlInputs: formattedControlInputs,
          controlOutputs: formattedControlOutputs,
          name: nodeData.displayName,
          id: nodeData.displayName,
      };
        // Prepare form data for dispatch
      const formData = {
          data: formattedData,
          user_id: 2
      };

        // Dispatch the action to add nodes
        await dispatch(AddNodes(formData));

        getAllNodeOnclick(); // Ensure all nodes are fetched after saving
        handleClose(); // Close the dialog or modal
      
    } catch (error) {
        console.error("Error while saving node data:", error);
        // Optional: Add user feedback logic here (e.g., alert or notification)
    }
  };

  const addInputField = () => {
    isInitialized.current = false;
    // Deep copy to avoid mutating state directly
    const newNodeData: any = structuredClone(nodeData);
    const newInputParam = {
      label: "New Input Param",
      name: `new-input-param-${Date.now()}`,
      type: "string",
      additionalParams: false,
      visibilityType: 'Parameter',
      description: '',
      placeholder: '',
      id: `new-input-param-${Date.now()}`,
    };
    newNodeData.inputParams.push(newInputParam);
    setNodeData(newNodeData); // Update nodeData state
    setFormData(newInputParam); // Update formData
    setTriggerEffect((prev) => !prev);
    handleRefresh()
  };

  const addInputControl = () => {
    // Deep copy to avoid mutating the state directly
    const newNodeData: any = structuredClone(nodeData); 
    const newOutput = {
      label: "default",
      name: `default-${Date.now()}`,
      id: `default-${Date.now()}`,   
    };
    // Ensure outputAnchors exists and is an array before pushing newOutput
    newNodeData.controlInputs.push(newOutput);
    isInitialized.current = false;
    setNodeData(newNodeData);
    setTriggerEffect((prev) => !prev);
    handleRefresh()
  };

  const addOutputField = () => {
    isInitialized.current = false;
    // Deep copy to avoid mutating the state directly
    const newNodeData: any = structuredClone(nodeData);
    const newOutput = {
      label: "New Output",
      name: `new-output-${Date.now()}`,
      type: "name",
      id: `new-output-${Date.now()}`, 
      visibilityType: 'Anchor'
    };
    newNodeData.outputAnchors.push(newOutput);
    setNodeData(newNodeData);
    setOutputFormData(newNodeData);
    setTriggerEffect((prev) => !prev);
    handleRefresh()
  };

  const deleteInputField = (id: any) => {
    isInitialized.current = false;
    // Clone the nodeData object to avoid direct mutations
    const newNodeData = structuredClone(nodeData);
    // Filter out the inputParam with the matching id
    newNodeData.inputParams = newNodeData.inputParams.filter((param: any) => param.id !== id); 
    // Update the state with the new nodeData
    setNodeData(newNodeData);
    setSelectedField(null)
    setTriggerEffect((prev) => !prev);
    handleRefresh()
  };

  const deleteInputAnchor = (id: any) => {
    isInitialized.current = false;
    // Clone the nodeData object to avoid direct mutations
    const newNodeData = structuredClone(nodeData);
    // Filter out the inputParam with the matching id
    newNodeData.inputParams = newNodeData.inputAnchors.filter((param: any) => param.id !== id); 
    // Update the state with the new nodeData
    setNodeData(newNodeData);
    setSelectedField(null)
    setTriggerEffect((prev) => !prev);
    handleRefresh()
  };

  const deleteOutputField = (id: any) => {
    isInitialized.current = false;
    // Clone the nodeData object to avoid direct mutations
    const newNodeData = structuredClone(nodeData);
    // Filter out the inputParam with the matching id
    newNodeData.outputAnchors = newNodeData.outputAnchors.filter((param: any) => param.id !== id); 
    // Update the state with the new nodeData
    setNodeData(newNodeData);
    setSelectedOutputField(null)
    setTriggerEffect((prev) => !prev);
    handleRefresh()
  };

  const deleteOutputControl = (id: any) => {
    isInitialized.current = false;
    // Clone the nodeData object to avoid direct mutations
    const newNodeData = structuredClone(nodeData);
    // Filter out the inputParam with the matching id
    newNodeData.controlOutputs = newNodeData.controlOutputs.filter((param: any) => param.id !== id); 
  // Update the state with the new nodeData
  setNodeData(newNodeData);
  setSelectedField(null);
  setTriggerEffect((prev) => !prev);
  handleRefresh();
  };

  const addOutputControl = () => {
    // Deep copy to avoid mutating the state directly
  const newNodeData: any = structuredClone(nodeData); 
  const newOutput = {
    label: "default",
    name: `default-${Date.now()}`,
    id: `default-${Date.now()}`,   
  };
  // Ensure outputAnchors exists and is an array before pushing newOutput
  newNodeData.controlOutputs.push(newOutput);
  isInitialized.current = false;
  setNodeData(newNodeData);
  setTriggerEffect((prev) => !prev);
  handleRefresh()
  };

  useEffect(() => {
    if (!isInitialized.current && reactFlowInstance) {
      const newNodeId = getUniqueNodeId(nodeData, reactFlowInstance.getNodes());
      const newNode = {
        id: newNodeId,
        position: { x: 150, y: 100 },
        type: 'custom',
        data: initNode(nodeData, newNodeId),
      };
  
      setNodes((nds) => nds.concat(newNode));
      handleRefresh();
      isInitialized.current = true;
    }
  }, [triggerEffect, nodeData, reactFlowInstance, setNodes]);


  const onChange = React.useCallback((val, viewUpdate) => {
    setSuccessLogicValue(val);
  }, []);


  const [refreshKey, setRefreshKey] = useState(1);

  const handleRefresh = () => {
    setRefreshKey((prevKey) => prevKey + 1); // Increment refresh key to force re-render
  };
  

  return (
    <div>
      <div className={`fixed inset-0 bg-black bg-opacity-50 opacity-100 z-50 ${openToolEditModal ? 'flex' : 'hidden'}`}>
        <div className="relative w-full h-full bg-white flex flex-col">
        <div className="flex w-full flex-row gap-6 items-center justify-between md:p-4">
            <div className='flex flex-row gap-4'>
                <Button
                        variant="outline"
                        size="sm"
                      
                        className="inline-flex items-center"
                        onClick={handleClose}
                    >
                         <IconX /> 
                    </Button>
                    <Input
                      name="displayName"
                      value={nodeData.displayName}
                      onChange={handleNodeChange}
                      className="w-48"
                      placeholder='Node Name'
                    />
            </div> 
              <Button  onClick={handleSave}>
              {checkSaved ? "Saved" : 'Save'}
            </Button>

          </div>
          <div className="flex flex-grow">
            <div className="w-4/6 h-full flex flex-col items-center border-r">
            <ReactFlow
        key={refreshKey} // Use the refresh key to force re-render
        nodes={nodes}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
      >
                  <Background color="#aaa" gap={16} />
                </ReactFlow>
            </div>
            <div className="w-3/4 h-full">
            <Tabs defaultValue="Tool Logic" >
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="Tool Logic">Tool Logic</TabsTrigger>
              <TabsTrigger value="Params">Params</TabsTrigger>
              <TabsTrigger value="Output">Output</TabsTrigger>
              <TabsTrigger value="Info">Info</TabsTrigger>
            </TabsList>
      <TabsContent value="Tool Logic">
      <div className='md:px-8 md:py-4'>
        <h3 className="agent-title">Tool Logic</h3>
  
      <CodeMirror
        value={successLogicValue}
        height="70vh"
        className='h-full'
        extensions={[javascript({ jsx: true })]}
        onChange={onChange}
        
      />
  </div>
      </TabsContent>
             <TabsContent value="Params">
               <div className="grid grid-cols-5 gap-4 p-4">
                  <div className="col-span-2 border-r border-gray-300 p-4">
                    <Card>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <span>Control</span>
                          <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline">
                              <IconCirclePlus onClick={addInputControl} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Add Control</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        </div>
                      </CardHeader>
                      <CardContent>
                      {(nodeData?.controlInputs || []).map((param: any, index: number) => 
                        param && (
                          <Card
                            key={index}
                            className="cursor-pointer hover:bg-gray-200 mb-2 p-2 flex justify-between items-center"
                            onClick={() => SelectedInputControl(param)}
                          >
                            <span>{param.name}</span>
                             {!(index === 0 && param.name === "default") && (
                                <IconTrash onClick={() => deleteInputControl(param.id)} />
                              )}
                          </Card>
                        )
                      )}
                      </CardContent>
                    </Card>

                    <Card className='mt-4'>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <span>Inputs</span>
                          <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline">
                              <IconCirclePlus onClick={addInputField} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Add Inputs</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        </div>
                      </CardHeader>
                      <CardContent>
                        {(nodeData?.inputParams || []).map((param: any, index: number) => (
                            param && (
                              <div
                                key={param?.id || index}
                                className="cursor-pointer hover:bg-gray-200 mb-2 p-2 flex justify-between items-center border rounded"
                                onClick={() => listField(param)}
                              >
                                <span>{param?.label || 'Unnamed Param'}</span>
                                <IconTrash
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering parent onClick
                                    deleteInputField(param?.id);
                                  }}
                                  className="text-red-500 cursor-pointer"
                                />
                              </div>
                            )
                          ))}
                          
                          {(nodeData?.inputAnchors || []).map((param: any, index: number) => (
                            param && (
                              <div
                                key={param?.id || index}
                                className="cursor-pointer hover:bg-gray-200 mb-2 p-2 flex justify-between items-center border rounded"
                                onClick={() => listField(param)}
                              >
                                <span>{param?.label || 'Unnamed Anchor'}</span>
                                <IconTrash
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering parent onClick
                                     deleteInputAnchor(param?.id);
                                  }}
                                  className="text-red-500 cursor-pointer"
                                />
                              </div>
                            )
                          ))}
                      </CardContent>
                    </Card>
                  </div>


                  <div className="col-span-3 p-4">
                    <Card className='p-4'>
                  {selectedField && ( <>
                  <form onSubmit={(e) => e.preventDefault()}>
                        
                    <div className="grid w-full max-w-sm items-center gap-1.5">
                          <Label htmlFor="picture">Label</Label>
                          <Input
                            name="label"
                            value={formData.label}
                            onChange={handleChangeForm}
                            className="mb-4"
                          />
                      </div>

                      <div className="grid w-full max-w-sm items-center gap-1.5">
                          <Label htmlFor="description">Description</Label>
                          <Textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChangeForm}
                            className="mb-4"
                           
                          />
                      </div>

                      <div className="grid w-full max-w-sm items-center gap-1.5">
                          <Label htmlFor="picture">Placeholder</Label>
                          <Input
                            name="placeholder"
                            value={formData.placeholder}
                            onChange={handleChangeForm}
                            className="mb-4"
                          />
                      </div>
                     
                  {formData.visibilityType === 'Parameter' && (    
                    <div className="grid w-full max-w-sm items-center gap-1.5">
                        <Label htmlFor="type">Type</Label>
                        <select
                            className="border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full block p-2.5 focus:outline-none"
                            name="type"
                          value={formData.type}
                          onChange={handleChangeForm}
                        > 
                            <option value="string">String</option>
                            <option value="password">password</option>
                            <option value="file">File</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean</option>
                            <option value="json">JSON</option>
                        </select>
                      </div>
                    )}

                      <div className="grid w-full max-w-sm items-center gap-1.5 mt-2">
                        <Label htmlFor="visibilityType">Visibility Type</Label>
                        <select
                            className="border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full block p-2.5 focus:outline-none"
                            name="visibilityType"
                          value={formData.visibilityType}
                          onChange={handleChangeForm}
                        > 
                            <option value="Parameter">Parameter</option>
                            <option value="Anchor">Anchor</option>
                        </select>
                      </div>
                      {formData.visibilityType === "Anchor" && (
                        <div className="grid w-full max-w-sm items-center gap-1.5 mt-2">
                        <Label htmlFor="type">Custom Type</Label>
                        <Input
                          name="type"
                          placeholder="Enter custom value"
                          value={formData.type}
                          onChange={handleChangeForm}
                        />
                        </div>
                      )}
                    <div className="flex flex-row w-full max-w-sm items-center gap-1.5 mt-4 ">
                      <label htmlFor="additionalParams" className='w-1/2'>Additional Params</label>
                      <Input
                        type="checkbox"
                        name="additionalParams"
                        checked={formData.additionalParams}
                        onChange={handleChangeForm}
                        className="w-1/2"
                      />
                    </div>

                      </form>
                  </>
                )}



              {selectedInputControl && (
                    <>
                     <form onSubmit={(e) => e.preventDefault()}> 
                        <div className="grid w-full max-w-sm items-center gap-1.5">
                              <Label htmlFor="picture">Label</Label>
                              <Input
                                name="label"
                                value={inputControlFormData.label}
                                onChange={handleInputConstrolChange}
                                className="mb-4"
                              />
                          </div>
                          <div className="grid w-full max-w-sm items-center gap-1.5">
                              <Label htmlFor="picture">Name</Label>
                              <Input
                                name="name"
                                value={inputControlFormData.name}
                               onChange={handleInputConstrolChange}
                                className="mb-4"
                              />
                          </div>
                          </form>
                    </>
                    )}


                    </Card>
                  </div>
                </div>
      </TabsContent>
      <TabsContent value="Output">
      <div className="grid grid-cols-5 gap-4 p-4">
                  <div className="col-span-2 border-r border-gray-300 p-4">
                    <Card>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <span>Control</span>
                          <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline">
                              <IconCirclePlus onClick={addOutputControl} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Add Control</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        </div>
                      </CardHeader>
                      <CardContent>
                      {(nodeData?.controlOutputs || []).map((param: any, index: number) => 
                        param && (
                          <Card
                            key={index}
                            className="cursor-pointer hover:bg-gray-200 mb-2 p-2 flex justify-between items-center"
                            onClick={() => SelectedOutputControl(param)}
                          >
                            <span>{param.name}</span>
                            <IconTrash onClick={() => deleteOutputControl(param.id)} />
                          </Card>
                        )
                      )}
                      </CardContent>
                    </Card>

                    <Card className='mt-4'>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <span>Outputs</span>
                          <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline">
                              <IconCirclePlus onClick={addOutputField} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Add Output</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        </div>
                      </CardHeader>
                      <CardContent>
                   
                      {nodeData?.outputAnchors?.map((param: any, index: number) => (
                          <div
                            key={param?.id || index} // Fallback to 'index' if 'id' is null/undefined
                            className="cursor-pointer hover:bg-gray-200 mb-2 p-2 flex justify-between items-center border rounded"
                            onClick={() => param && OutputlistField(param)} // Ensure 'param' exists
                          >
                            <span>{param?.label || 'Unnamed Field'}</span> {/* Fallback label */}
                            <IconTrash
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering parent onClick
                                param?.id && deleteOutputField(param.id); // Delete only if id exists
                              }}
                              className="text-red-500 cursor-pointer"
                            />
                          </div>
                        )) || (
                          <p className="text-gray-400">No fields available</p> // Handle empty/null case
                        )}


                      </CardContent>
                    </Card>
                  </div>


                  <div className="col-span-3 p-4">
                    
                  <Card className='p-4'>
                {selectedOutputField && ( 
                  <>
                        <form onSubmit={(e) => e.preventDefault()}>
                              
                          <div className="grid w-full max-w-sm items-center gap-1.5">
                                <Label htmlFor="picture">Label</Label>
                                <Input
                                  name="label"
                                  value={outputFormData.label}
                                  onChange={handleOutputChange}
                                  className="mb-4"
                                />
                            </div>

                            <div className="grid w-full max-w-sm items-center gap-1.5">
                                <Label htmlFor="description">Output Name</Label>
                                <Input
                                  name="description"
                                  value={outputFormData.name}
                                  onChange={handleOutputChange}
                                  className="mb-4"
                                />
                            </div>

                          {/* <div className="grid w-full max-w-sm items-center gap-1.5">
                              <Label htmlFor="type">Type</Label>
                              <select
                                  className="border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full block p-2.5 focus:outline-none"
                                  name="type"
                                value={formData.type}
                                onChange={handleChangeForm}
                              > 
                                  <option value="string">String</option>
                                  <option value="password">password</option>
                                  <option value="options">Options</option>
                                  <option value="file">File</option>
                                  <option value="number">Number</option>
                                  <option value="boolean">Boolean</option>
                                  <option value="json">JSON</option>
                              </select>
                            </div>  */}

                          <div className="grid w-full max-w-sm items-center gap-1.5 mt-2">
                              <Label htmlFor="visibilityType">Visibility Type</Label>
                              <select
                                  className="border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full block p-2.5 focus:outline-none"
                                  name="visibilityType"
                                value={outputFormData.visibilityType}
                                onChange={handleOutputChange}
                              > 
                                  <option value="Parameter">Parameter</option>
                                  <option value="Anchor">Anchor</option>
                              </select>
                            </div>


                        {outputFormData?.visibilityType === 'Parameter' && (    
                          <div className="grid w-full max-w-sm items-center gap-1.5">
                              <Label htmlFor="type">Type</Label>
                              <select
                                  className="border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full block p-2.5 focus:outline-none"
                                  name="type"
                                value={outputFormData.type}
                                onChange={handleOutputChange}
                              > 
                                  <option value="string">String</option>
                                  <option value="password">password</option>
                                  <option value="file">File</option>
                                  <option value="number">Number</option>
                                  <option value="boolean">Boolean</option>
                                  <option value="json">JSON</option>
                                  <option value="options">Options</option>
                              </select>
                            </div>
                          )}
                            {outputFormData.visibilityType === "Anchor" && (
                              <div className="grid w-full max-w-sm items-center gap-1.5 mt-2">
                              <Label htmlFor="type">Custom Type</Label>
                              <Input
                                name="type"
                                placeholder="Enter custom value"
                                value={outputFormData.type}
                                onChange={handleOutputChange}
                              />
                              </div>
                            )}                      
                            </form>
                              </>
                            )}


{selectedOutputControl && (
                    <>
                     <form onSubmit={(e) => e.preventDefault()}> 
                        <div className="grid w-full max-w-sm items-center gap-1.5">
                              <Label htmlFor="picture">Label</Label>
                              <Input
                                name="label"
                                value={outputControlFormData.label}
                                onChange={handleOutputConstrolChange}
                                className="mb-4"
                              />
                          </div>
                          <div className="grid w-full max-w-sm items-center gap-1.5">
                              <Label htmlFor="picture">Name</Label>
                              <Input
                                name="name"
                                value={outputControlFormData.name}
                               onChange={handleOutputConstrolChange}
                                className="mb-4"
                              />
                          </div>
                          </form>
                    </>
                    )}

                      </Card>
                  </div>
                </div>
      </TabsContent>
      <TabsContent value="Info">
      
        <Card className='p-4 border-none ml-16 mr-16'>
                  <div className="grid w-full items-center gap-1.5">
                          <Label htmlFor="picture">Node Name</Label>
                          <Input
                            name="displayName"
                            value={nodeData.displayName}
                            onChange={handleNodeChange}
                            className="mb-4"
                          />
                      </div>

                      <div className="grid w-full  items-center gap-1.5">
                          <Label htmlFor="picture">Icon</Label>
                          <Input
                            name="logoUrl"
                            value={nodeData.logoUrl}
                            onChange={handleNodeChange}
                            className="mb-4"
                          />
                      </div>

                  <div className="grid w-full items-center gap-1.5">
                          <Label htmlFor="description">Description</Label>
                          <Textarea
                            name="description"
                            value={nodeData.description}
                            onChange={handleNodeChange}
                            className="mb-4"
                            rows={5}
                          />
                      </div>
          </Card>
      </TabsContent>
      {/* <TabsContent value="AI Update">
       <div className="p-4">AI Update</div>
        </TabsContent> */}
    </Tabs>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolEditModal;
